import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useHolidayCatalog } from '@/hooks/useHolidayCatalog';
import { useCustomHolidays } from '@/hooks/useCustomHolidays';
import { useUserConfigState } from '@/context/UserConfigContext';
import type { CatalogHoliday } from '@/types/holiday';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CustomHolidayDialog } from './components/CustomHolidayDialog';

export interface HolidayMultiSelectProps {
  selectedCatalogIds: string[];
  selectedCustomIds: string[];
  onCatalogChange: (ids: string[]) => void;
  onCustomChange: (ids: string[]) => void;
  /** undefined = user config context; null = user scope; string = client context */
  clientId?: string | null;
  /** Optional controlled state for the mobile selection sheet */
  sheetOpen?: boolean;
  onSheetOpenChange?: (open: boolean) => void;
}

export function HolidayMultiSelect({
  selectedCatalogIds,
  selectedCustomIds,
  onCatalogChange,
  onCustomChange,
  clientId,
  sheetOpen: sheetOpenProp,
  onSheetOpenChange,
}: HolidayMultiSelectProps) {
  const { countryCodes } = useUserConfigState();
  const { data: catalog = [], isLoading: catalogLoading } = useHolidayCatalog();
  const { data: customs = [] } = useCustomHolidays();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [internalSheetOpen, setInternalSheetOpen] = useState(false);
  const sheetOpen = sheetOpenProp ?? internalSheetOpen;
  const setSheetOpen = onSheetOpenChange ?? setInternalSheetOpen;

  const catalogByCountry = useMemo(() => {
    const byCountry = new Map<string, CatalogHoliday[]>();
    for (const h of catalog) {
      const arr = byCountry.get(h.countryCode) ?? [];
      arr.push(h);
      byCountry.set(h.countryCode, arr);
    }
    return byCountry;
  }, [catalog]);

  const selectedCatalogItems = useMemo(
    () => catalog.filter(h => selectedCatalogIds.includes(h.id)),
    [catalog, selectedCatalogIds],
  );
  const selectedCustomItems = useMemo(
    () => customs.filter(c => selectedCustomIds.includes(c.id)),
    [customs, selectedCustomIds],
  );
  const totalSelected = selectedCatalogItems.length + selectedCustomItems.length;

  const handleToggleCatalog = (id: string) => {
    if (selectedCatalogIds.includes(id)) {
      onCatalogChange(selectedCatalogIds.filter(x => x !== id));
    } else {
      onCatalogChange([...selectedCatalogIds, id]);
    }
  };

  const handleToggleCustom = (id: string) => {
    if (selectedCustomIds.includes(id)) {
      onCustomChange(selectedCustomIds.filter(x => x !== id));
    } else {
      onCustomChange([...selectedCustomIds, id]);
    }
  };

  const showEmptyBanner = !catalogLoading && catalog.length === 0 && countryCodes.length > 0;

  if (showEmptyBanner) {
    return (
      <div className="rounded border border-muted p-4 text-sm text-muted-foreground">
        No holidays loaded for {countryCodes.join(', ')} yet. They'll appear within a few minutes.
      </div>
    );
  }

  const renderCatalogItem = (h: CatalogHoliday) => (
    <li key={h.id} className="min-h-[44px] flex items-center break-inside-avoid">
      <label className="flex items-center gap-2 cursor-pointer flex-1 min-h-[44px]">
        <Checkbox
          checked={selectedCatalogIds.includes(h.id)}
          onCheckedChange={() => handleToggleCatalog(h.id)}
          aria-label={h.name}
        />
        <span className="text-sm">{h.name}</span>
      </label>
    </li>
  );

  const customItems = customs.map(c => (
    <li key={`custom-${c.id}`} className="min-h-[44px] flex items-center break-inside-avoid">
      <label className="flex items-center gap-2 cursor-pointer flex-1 min-h-[44px]">
        <Checkbox
          checked={selectedCustomIds.includes(c.id)}
          onCheckedChange={() => handleToggleCustom(c.id)}
          aria-label={c.name}
        />
        <span className="text-sm">{c.name}</span>
      </label>
    </li>
  ));

  const addCustomButton = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="mt-2 text-sm text-primary"
      onClick={() => setDialogOpen(true)}
    >
      + Add custom holiday
    </Button>
  );

  const catalogList = (columnClass: string) =>
    countryCodes.length > 1 ? (
      <>
        {Array.from(catalogByCountry.entries()).map(([cc, rows]) => (
          <section key={cc}>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">{cc}</h4>
            <ul className={`space-y-0.5 ${columnClass}`}>{rows.map(renderCatalogItem)}</ul>
          </section>
        ))}
        {customs.length > 0 && (
          <section>
            <ul className={`space-y-0.5 ${columnClass}`}>{customItems}</ul>
          </section>
        )}
        {addCustomButton}
      </>
    ) : (
      <section>
        <ul className={`space-y-0.5 ${columnClass}`}>
          {catalog.map(renderCatalogItem)}
          {customItems}
        </ul>
        {addCustomButton}
      </section>
    );

  return (
    <div>
      {/* Desktop view — inline multi-column list */}
      <div className="hidden md:block">
        {catalogList('md:columns-2 lg:columns-3 md:gap-x-6')}
      </div>

      {/* Mobile view — plain chip list, no card */}
      <div className="md:hidden">
        {totalSelected === 0 ? (
          <p className="text-xs text-muted-foreground">No holidays selected.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedCustomItems.map(c => (
              <span
                key={`c-${c.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/15 text-primary-text px-2 text-xs font-medium min-h-[28px]"
              >
                {c.name}
                <button
                  type="button"
                  aria-label={`Remove ${c.name}`}
                  className="touch-target-pseudo inline-flex items-center justify-center rounded hover:bg-primary/25"
                  onClick={() => handleToggleCustom(c.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {selectedCatalogItems.map(h => (
              <span
                key={`h-${h.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/15 text-primary-text px-2 text-xs font-medium min-h-[28px]"
              >
                {h.name}
                <button
                  type="button"
                  aria-label={`Remove ${h.name}`}
                  className="touch-target-pseudo inline-flex items-center justify-center rounded hover:bg-primary/25"
                  onClick={() => handleToggleCatalog(h.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="bottom"
          onClose={() => setSheetOpen(false)}
          dragHandleVariant="compact"
          className="max-h-[88vh] flex flex-col p-0"
        >
          <SheetHeader className="px-4 pt-2 pb-3 border-b border-border/60 text-left">
            <SheetTitle className="font-heading uppercase tracking-wide text-base">
              Select Holidays
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-4 py-3">
            {catalogList('')}
          </div>

          <div className="px-4 py-3 border-t border-border/60">
            <Button
              type="button"
              variant="default"
              className="w-full"
              onClick={() => setSheetOpen(false)}
            >
              Done
              {totalSelected > 0 && (
                <span className="ml-1 opacity-70">· {totalSelected}</span>
              )}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <CustomHolidayDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        clientId={clientId ?? null}
        editingHoliday={null}
      />
    </div>
  );
}
